/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {useEffect, useRef} from 'react';
import {loadModules} from 'esri-loader';
import isNil from 'lodash.isnil';
import {error} from '@ion/core/src/logger/logger';
import {CVSAssetType} from '@ion/core/src/types/entities';
import {onSelect} from '../helpers/simpleMapUtils';
import useZoomTo from '../hooks/useZoomTo';
import {createMarkers} from '../markers/AssetMarkers';
import {MapColorModes} from '../types/types';

// Unique identifier for the asset layer.
export const ASSET_LAYER_ID = 'map-layer-asset';

// Default View Extent Factor.
export const VIEW_EXTENT_FACTOR = 1.25;

// View default center point and zoom level (world view).
export const VIEW_CENTER_DEFAULT = [0, 25];
export const VIEW_ZOOM_DEFAULT   = 2;

// View default zoom level when a single asset is selected.
export const VIEW_ZOOM_SINGLE_POINT = 17;

// Defines the props for the AssetLayer component.
export type AssetLayerProps = {
  map: __esri.Map;
  view: __esri.MapView;
  assets?: CVSAssetType[];
  selectedAsset?: CVSAssetType;
  enableMarkerClick?: boolean;
  includeIcon?: boolean;
  setSelected?: Function;
  color?: MapColorModes;
  useZoomToPoints?: boolean;
};

// Creates a set of map points from a set of asset LAT/LNG coordinates.
const createPoints = (assets: CVSAssetType[]) => {
  return assets?.map(asset => {
    const {longitude, latitude} = asset;
    return !isNil(latitude) && !isNil(longitude)
      ? {
        ...asset,
        geometry : {
          type        : 'Point',
          coordinates : [longitude, latitude]
        }
      }
      : undefined;
  }).filter(asset => asset); // remove empty array entries before returning
};

/**
 * Defines the AssetLayer which manages rendering a layer containing individual
 * Assets within a SimpleMap.
 *
 * ```typescript
 *
 *   import {AssetLayer} from '@ion/features/src/maps/layers';
 *
 *   const layers = [
 *     {
 *       id        : 'map-layer-asset',
 *       component : AssetLayer,
 *       props     : {
 *         assets,
 *         selectedAsset
 *       }
 *     }
 *   ];
 *
 * ```
 *
 */
export const AssetLayer = ({
  map,
  view,
  assets,
  selectedAsset,
  setSelected,
  color = MapColorModes.dark,
  enableMarkerClick = false,
  includeIcon = false,
  useZoomToPoints = false
}: AssetLayerProps) => {
  const assetLayerRef = useRef(null);
  const zoomTo        = useZoomTo();

  const zoomToDefault = () => {
    view.goTo({
      center : VIEW_CENTER_DEFAULT,
      zoom   : VIEW_ZOOM_DEFAULT
    });
  };

  const zoomToPoint = (point) => {
    const {longitude, latitude} = point;
    view.goTo({
      center : [longitude, latitude],
      zoom   : VIEW_ZOOM_SINGLE_POINT
    });
  };

  const zoomToPoints = (points) => {
    const extent = zoomTo(points, VIEW_EXTENT_FACTOR);
    extent && view.goTo(extent);
  };

  useEffect(() => {
    if (!assets?.length) {
      if (assetLayerRef.current) {
        assetLayerRef.current.graphics.removeAll();
      }
      return;
    }
    loadModules([
      'esri/layers/GraphicsLayer',
      'esri/Graphic'
    ])
      .then(([GraphicsLayer, Graphic]) => {
        if (!assetLayerRef.current) {
          assetLayerRef.current = new GraphicsLayer({
            id      : ASSET_LAYER_ID,
            title   : ASSET_LAYER_ID,
            visible : true
          });
          map.add(assetLayerRef.current);
          map.layers.reorder(assetLayerRef.current, 1);
        }
        assetLayerRef.current.graphics.removeAll();
        const points = createPoints(selectedAsset ? [selectedAsset] : assets);
        if (points.length) {
          const markers = createMarkers({
            assets     : points as any[],
            markerOnly : false,
            includeIcon,
            Graphic,
            color,
            selectedAsset
          });
          markers.length && assetLayerRef.current.graphics.addMany(markers);
          if (selectedAsset) {
            if (useZoomToPoints) {
              zoomToPoints(points);
              view.zoom = VIEW_ZOOM_SINGLE_POINT;
            } else {
              zoomToPoint(points[0]);
            }
          } else {
            zoomToPoints(points);
            enableMarkerClick && onSelect(view, ASSET_LAYER_ID, (item) => setSelected(item));
          }
        } else {
          zoomToDefault();
        }
      })
      .catch((err) => {
        error(err);
      });

    return function() {
      view.graphics.removeAll();
    };
  }, [view, assets, enableMarkerClick, selectedAsset, assetLayerRef]);

  return null;
};

export default AssetLayer;
